import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
import type { AddressQuery } from '../types/house';
import { isValidPostcode } from '../utils/house';
import { DEMO_ADDRESS, isDemoQuery } from '../data/demo';
interface AddressFormProps {
  tone?: 'light' | 'dark';
  layout?: 'inline' | 'stacked';
  onSubmit?: (query: AddressQuery) => void;
  submitLabel?: string;
  idPrefix?: string;
  prefillDemo?: boolean;
}
export function AddressForm({
  tone = 'light',
  layout = 'inline',
  onSubmit,
  submitLabel = 'Scan mijn woning',
  idPrefix = 'addr',
  prefillDemo = true
}: AddressFormProps) {
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState(prefillDemo ? DEMO_ADDRESS.postcode : '');
  const [number, setNumber] = useState(prefillDemo ? DEMO_ADDRESS.number : '');
  const [addition, setAddition] = useState(prefillDemo ? DEMO_ADDRESS.addition : '');
  const [error, setError] = useState<string | null>(null);
  const current: AddressQuery = {
    postcode,
    number,
    addition
  };
  const showingDemo = isDemoQuery(current);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidPostcode(postcode)) {
      setError('Vul een geldige postcode in, bijvoorbeeld 3811 AB.');
      return;
    }
    if (!/^\d{1,5}$/.test(number.trim())) {
      setError('Vul een huisnummer in.');
      return;
    }
    setError(null);
    const query: AddressQuery = {
      postcode: postcode.trim(),
      number: number.trim(),
      addition: addition.trim()
    };
    if (onSubmit) {
      onSubmit(query);
      return;
    }
    const params = new URLSearchParams({
      postcode: query.postcode.replace(/\s/g, ''),
      nr: query.number
    });
    if (query.addition) params.set('toev', query.addition);
    navigate(`/scan?${params.toString()}`);
  };
  const dark = tone === 'dark';
  const stacked = layout === 'stacked';
  const field = `h-14 w-full rounded-xl border px-4 text-[16px] outline-none transition-colors duration-150 placeholder:text-ink-muted/70 focus:border-ink ${dark ? 'border-transparent bg-canvas text-ink' : 'border-line bg-canvas/60 text-ink focus:bg-white'}`;
  const label = `mb-1.5 block text-[13px] font-medium ${dark ? 'text-canvas/70' : 'text-ink-muted'}`;
  return <form onSubmit={handleSubmit} noValidate aria-describedby={error ? `${idPrefix}-error` : undefined} data-id="element-14" data-mp="2w61vmdg9j31n">
      <div className={stacked ? 'grid grid-cols-[1fr_96px] gap-2' : 'grid grid-cols-[1fr_96px_80px] gap-2 sm:grid-cols-[1fr_104px_84px_auto] sm:items-end'} data-id="element-15" data-mp="23jqm2daohrsp">
        <div className={stacked ? 'col-span-2' : ''} data-id="element-16" data-mp="2da21ox3q5pff">
          <label htmlFor={`${idPrefix}-postcode`} className={label} data-id="element-17" data-mp="2dav0nbwfzan0">Postcode</label>
          <input id={`${idPrefix}-postcode`} className={`${field} uppercase`} placeholder="3811 AB" autoComplete="postal-code" value={postcode} maxLength={7} onChange={e => setPostcode(e.target.value)} aria-invalid={!!error && !isValidPostcode(postcode)} data-id="element-18" data-mp="28h8xcac64j3v" />
        </div>
        <div data-id="element-19" data-mp="3vpc9rvcplp">
          <label htmlFor={`${idPrefix}-number`} className={label} data-id="element-20" data-mp="3lx0fjcq6cu">Huisnummer</label>
          <input id={`${idPrefix}-number`} className={field} placeholder="12" inputMode="numeric" value={number} maxLength={5} onChange={e => setNumber(e.target.value.replace(/\D/g, ''))} data-id="element-21" data-mp="3hkd8yoa22h0v" />
        </div>
        <div data-id="element-22" data-mp="j0n4c70doijy">
          <label htmlFor={`${idPrefix}-addition`} className={label} data-id="element-23" data-mp="j0dfwnuzejgd">Toev.</label>
          <input id={`${idPrefix}-addition`} className={field} placeholder="A" value={addition} maxLength={4} onChange={e => setAddition(e.target.value)} data-id="element-24" data-mp="52ounex05aas" />
        </div>
        <button type="submit" className={`inline-flex h-14 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-volt px-6 text-[16px] font-semibold text-ink transition-colors duration-150 hover:bg-volt-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${stacked ? 'col-span-2 mt-2' : 'col-span-3 mt-1 sm:col-span-1 sm:mt-0'}`} data-id="element-25" data-mp="2puik0sq40tcq">
          {submitLabel}
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-26" data-mp="2vdsxosrvvsv8" />
        </button>
      </div>

      {error ? <p id={`${idPrefix}-error`} role="alert" className={`mt-3 text-sm font-medium ${dark ? 'text-volt' : 'text-danger'}`} data-id="element-27" data-mp="3sxnkbpifjauj">
          {error}
        </p> : showingDemo && <p className={`mt-3 inline-flex items-center gap-1.5 text-[13px] ${dark ? 'text-canvas/60' : 'text-ink-muted'}`} data-id="element-28" data-mp="3j3na598ajfey">
            <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-29" data-mp="27mf1ucuixf5i" />
            Demo-adres ingevuld: Sterrenschans 12, Veenendaal
          </p>}
    </form>;
}